import React, { useEffect, useState, useRef } from 'react';
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Tooltip } from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import { clampLineItemRemarks, LINE_ITEM_REMARKS_MAX_LENGTH } from '../../Utility/lineItemRemarksUtils';

export default function LineItemRemarksButton({
    enabled,
    value,
    readOnly,
    disabled,
    onSave,
    title,
}) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(value || '');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) setText(clampLineItemRemarks(value || ''));
    }, [open, value]);

    const focusCaretAtEnd = () => {
        const el = inputRef.current;
        if (!el) return;
        const len = (el.value ?? '').length;
        try {
            el.focus();
            if (typeof el.setSelectionRange === 'function') {
                el.setSelectionRange(len, len);
            }
        } catch {
            /* ignore selection errors on unsupported inputs */
        }
    };

    if (!enabled) return null;

    return (
        <>
            <Tooltip title={title || (readOnly ? 'View remarks' : 'Service remarks')}>
                <span>
                    <IconButton size="small" onClick={() => setOpen(true)} disabled={disabled} sx={{ p: 0.25, mr: 0.45 }}>
                        <CommentIcon fontSize="small" color={value ? 'primary' : 'action'} />
                    </IconButton>
                </span>
            </Tooltip>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                TransitionProps={{
                    onEntered: () => {
                        requestAnimationFrame(() => {
                            focusCaretAtEnd();
                            requestAnimationFrame(focusCaretAtEnd);
                        });
                    },
                }}
            >
                <DialogTitle>{readOnly ? 'Line item remarks' : 'Edit remarks'}</DialogTitle>
                <DialogContent>
                    <TextField
                        inputRef={inputRef}
                        margin="dense"
                        label={readOnly ? '' : 'Remarks'}
                        placeholder={readOnly ? '' : 'Enter remarks for this line item'}
                        fullWidth
                        multiline
                        minRows={readOnly ? 4 : 3}
                        value={text}
                        onChange={(e) => setText(clampLineItemRemarks(e.target.value))}
                        inputProps={{ maxLength: LINE_ITEM_REMARKS_MAX_LENGTH }}
                        InputProps={{ readOnly: !!readOnly }}
                        variant="outlined"
                        sx={{ mt: 1 }}
                        helperText={readOnly ? undefined : `${text.length}/${LINE_ITEM_REMARKS_MAX_LENGTH}`}
                        FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                    />
                </DialogContent>
                <DialogActions>
                    {readOnly ? (
                        <Button onClick={() => setOpen(false)}>Close</Button>
                    ) : (
                        <>
                            <Button onClick={() => setOpen(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    if (onSave) onSave(clampLineItemRemarks(text));
                                    setOpen(false);
                                }}
                            >
                                Save
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
